import { MutableRefObject } from "react"
import { array, is, object } from "superstruct"

import {
    SurveyAnswer,
    SurveyAnswerValueRecord,
    SurveyQuestion,
    SurveyQuestions,
    Choices,
    PriceCategory,
    SearchFormModel,
    FetchQuestionnaireProfilesResponse,
} from "../interface"
import { AnswerType } from "../constants"

import { compareConditions } from "./compareConditions"

import { Sex, SlotType } from "@utils/interfaces"
import { isObject } from "@utils/utils"
import { Analytics } from "@utils/analytics"
import { UserAccount } from "@utils/UserAccount"

export function findQuestionIndexById(data: SurveyQuestions, id: string) {
    return data.questions.findIndex((item) => item.id === id)
}

export function findQuestionById(data: SurveyQuestions, id: string) {
    return data.questions.find((item) => item.id === id)
}

export function findNextQuestion(
    questions: SurveyQuestions,
    questionId: string,
    answers: Record<string, SurveyAnswer>
) {
    const indexQuestion = findQuestionIndexById(questions, questionId)
    for (let i = indexQuestion + 1; i < questions.questions.length; i++) {
        if (
            !(
                questions.questions[i].visibility_conditions.length > 0 &&
                !compareConditions(questions.questions[i].visibility_conditions, answers)
            )
        )
            return questions.questions[i]
    }
}

export function findPrevQuestion(
    questions: SurveyQuestions,
    questionId: string,
    answers: Record<string, SurveyAnswer>
) {
    const indexQuestion = findQuestionIndexById(questions, questionId)

    for (let i = indexQuestion - 1; i >= 0; i--) {
        if (
            !(
                questions.questions[i].visibility_conditions.length > 0 &&
                !compareConditions(questions.questions[i].visibility_conditions, answers)
            )
        )
            return questions.questions[i]
    }
}

export function parseCity(choice: Choices | Choices[] | undefined): string {
    if (is(choice, array())) throw "parseCity requires only one answer"
    if (!choice) return ""
    return choice.title
}

export function parsePriceRange(choice: Choices | Choices[] | undefined) {
    if (is(choice, array())) throw "parsePriceRange requires only one answer"

    if (!choice)
        return {
            category: "any" as PriceCategory,
            min: undefined,
            max: undefined,
            label: "неважна",
        }

    const match = choice.ref.match(/^(\d*)_(\d*)$/)
    if (!match)
        return {
            category: choice.ref as PriceCategory,
            min: undefined,
            max: undefined,
            label: choice.title,
        }

    return {
        category: choice.ref as PriceCategory,
        min: match[1] ? parseInt(match[1]) : undefined,
        max: match[2] ? parseInt(match[2]) : undefined,
        label: choice.title,
    }
}

export function parseSex(choice: Choices | Choices[] | undefined) {
    if (is(choice, array())) throw "parseSex requires only one answer"
    const def = Sex.Any
    if (!choice) return def
    if (!(Object.values(Sex) as string[]).includes(choice.ref)) return def
    return choice.ref as Sex
}

export function parseSlotType(choice: Choices | Choices[] | undefined): SlotType {
    if (is(choice, array())) throw "parseSlotType requires only one answer"
    const def = SlotType.Online
    if (!choice) return def
    if (!(Object.values(SlotType) as string[]).includes(choice.ref)) return def
    return choice.ref as SlotType
}

export function parseCompetences(choice: Choices | Choices[] | undefined) {
    if (!is(choice, array())) throw "parseCompetences requires only multiple answers"
    const def = [] as number[]
    if (!choice) return def
    return choice?.map((c) => parseInt(c.ref)) ?? []
}

export function analyticsOnProfilesLoaded(
    psys: FetchQuestionnaireProfilesResponse,
    profilesLoadedEventSent: boolean,
    modelRef: MutableRefObject<SearchFormModel | undefined>
) {
    if (UserAccount.isSignedIn && !profilesLoadedEventSent)
        Analytics.psychologists_loaded(modelRef.current)

    Analytics.top_competence({
        ...psys.query.source.search_terms,
        ...(modelRef.current ?? {}),
    })

    if (psys.matching_count > 0) {
        Analytics.psychologists_fetched()
    } else {
        Analytics.no_psychologists_found()
    }
}

export function filterObject<T>(obj: T, valueName: string, value: number | string | boolean): T {
    const result = {} as T
    Object.entries(obj as ArrayLike<unknown>).forEach(([key, valueObj]) => {
        if ((valueObj as Record<string, string>)[valueName] < value)
            result[key as keyof T] = valueObj as T[keyof T]
    })

    return result
}

export function sendAnalyticNextQuestion(
    answers: Record<string, SurveyAnswer>,
    id: string,
    name: string,
    isB2b: boolean
): void {
    const answer = answers[id]
    if (!answer) return

    Analytics.event_survey_answer({
        isB2b,
        surveyName: name,
        pageNumber: answer.sequence_number,
        questionId: id,
        answer:
            answer.type !== AnswerType.short_text
                ? isObject(answer.value)
                    ? Object.keys(answer.value)
                          .map(function (key) {
                              const value = answer.value as SurveyAnswerValueRecord
                              return value[key] && key
                          })
                          .filter((item) => !!item)
                          .join(",")
                    : answer.value
                : undefined,
    })
}

export function sendAnalyticPrevQuestion(
    question: SurveyQuestion,
    name: string,
    isB2b: boolean,
    prevQuestion?: SurveyQuestion
): void {
    Analytics.event_survey_return({
        isB2b,
        surveyName: name,
        questionId: question.id,
        prevQuestionId: prevQuestion ? prevQuestion.id : undefined,
    })
}

export function getCompetenceTitles(
    surveyQuestions: SurveyQuestions,
    answers: Record<string, SurveyAnswer>
): string[] {
    const result: string[] = []

    const reportedProblemsQuestions = surveyQuestions.questions.filter(
        (question) => question.ref === "reported_problems"
    )
    if (reportedProblemsQuestions.length === 0) return result

    const pairs = reportedProblemsQuestions
        .map((q) => ({ question: q, answer: answers[q.id] }))
        .filter(
            (
                p
            ): p is {
                question: (typeof reportedProblemsQuestions)[number]
                answer: SurveyAnswer
            } => p.answer !== undefined
        )

    if (pairs.length === 0) return result

    const flattenChoices = (q: (typeof reportedProblemsQuestions)[number]): Choices[] =>
        q.choices.flatMap((choice) => (choice.choices ? choice.choices : [choice]))

    pairs.forEach(({ question, answer }) => {
        const choices = flattenChoices(question)

        if (is(answer.value, object())) {
            const selectedIds = Object.entries(answer.value)
                .filter(([_, v]) => v)
                .map(([k]) => k)

            choices.forEach((c) => {
                if (selectedIds.includes(c.id)) {
                    result.push(c.title)
                }
            })
        } else {
            const found = choices.find((c) => c.id === answer.value)
            if (found) {
                result.push(found.title)
            }
        }
    })

    return result
}
